'use client'
import { type FC, useState } from 'react'

import { LightbulbIcon } from '@/components/icons/lightbulb'
import { StarAiIcon } from '@/components/icons/starAi'
import { useConfig } from '@/contexts/configProvider'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '../dialog'
import { toast } from '../toast'

import { SimilaritiesCards } from './components/similaritiesCards'

interface ISimilarityBadgeProps {
  count: number
  className?: string
  ideaId: string
  onClickCard: (id: string) => void
}

export const SimilarityBadge: FC<ISimilarityBadgeProps> = ({
  count,
  className = '',
  ideaId,
  onClickCard,
}) => {
  const { config } = useConfig()
  const [isOpen, setIsOpen] = useState(false)

  const showToastNotEnabled = () => {
    toast({
      title: 'Serviço de IA não habilitado',
      variant: 'destructive',
    })
  }

  if (!config?.hasAIService) {
    return (
      <button
        type="button"
        className={`flex cursor-pointer items-center justify-center gap-1.5 rounded-md px-1 py-0.5 ${className}`}
        style={{
          background: 'transparent',
          border: '1px solid #A2397D',
          borderRadius: '6px',
        }}
        onClick={(e) => {
          e.stopPropagation()
          showToastNotEnabled()
        }}
      >
        <LightbulbIcon width={18} height={18} gradient />
        <span
          style={{
            fontWeight: 500,
            fontSize: 14,
            color: '#323338cd',
          }}
        >
          {count.toString().padStart(2, '0')}
        </span>
      </button>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div
          className={`flex cursor-pointer items-center justify-center gap-1.5 rounded-md px-1 py-0.5 ${className}`}
          style={{
            background: 'transparent',
            border: '1px solid #A2397D',
            borderRadius: '6px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <LightbulbIcon width={18} height={18} gradient />
          <span
            style={{
              fontWeight: 500,
              fontSize: 14,
              color: '#323338cd',
            }}
          >
            {count.toString().padStart(2, '0')}
          </span>
        </div>
      </DialogTrigger>

      <DialogContent
        className="flex max-h-[80vh] w-[90vw] max-w-[1000px] flex-col p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center gap-2.5">
          <StarAiIcon width={32} height={32} gradient />
          <DialogTitle className="m-0 text-[22px] font-semibold">
            Iniciativas similares
          </DialogTitle>
        </div>
        <DialogDescription className="mb-4 text-[15px] text-[#323338cd]">
          Confira abaixo se já existem iniciativas semelhantes à que você está
          avaliando. Você pode <b>vinculá-las</b> e colaborar por meio de
          comentários ou <b>convidar os participantes</b> para integrar a mesma
          equipe.
        </DialogDescription>
        <div className="mb-[18px] flex items-center gap-2">
          <LightbulbIcon width={24} height={24} gradient />
          <span className="text-[17px] font-medium text-[#323338]">
            Ideias encontradas:{' '}
            <span className="font-bold text-[#A2397D]">{count}</span>
          </span>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <SimilaritiesCards ideaId={ideaId} onClickCard={onClickCard} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SimilarityBadge
